import { lazy, memo, useEffect, useMemo } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { useAuth } from 'react-oidc-context';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { createXPointIntegration } from '@endeavour/xpoint';
import { createSportNCoNativeXPointIntegration } from '@endeavour/xpoint/dist/snc-native-app';

import { config } from '../../config/config';
import { accountStore } from '../account/store/account.store';
import { PaymentsProvider } from '../cashier/containers/payments-provider/payments-provider';
import { useCookieBanner } from '../documents/hooks/use-cookie-banner';
import {
    EventingServiceProvider,
    useEventDispatcher,
} from '../eventing/components/eventing-service-provider/eventing-service-provider';
import { useEventListener } from '../eventing/hooks/use-event-listener';
import { useResize } from '../eventing/hooks/use-resize';
import { useTransactionComplete } from '../eventing/hooks/use-transaction-complete';
import { EventName } from '../eventing/interfaces/events.interface';
import { sportncoEventListener } from '../eventing/listeners/sportnco';
import { xtremePushEventListener } from '../eventing/listeners/xtreme-push';
import { useGeoLocationToast } from '../geolocation/hooks/use-geolocation-toast';
import { GeolocationContextProvider } from '../geolocation/util/geolocation-context';
import { VerificationContextProvider } from '../kyc-verification/util/verification-context';
import { ScrollToTop } from '../shared/components/scroll-to-top/scroll-to-top';
import { useFullLocation } from '../shared/hooks/use-full-location';
import { useFullscreen } from '../shared/hooks/use-fullscreen';
import { useSetSource } from '../shared/hooks/use-set-source';
import type { FCWithChildren } from '../shared/utils/component.interface';
import { getXtremePush } from '../xtreme-push/utils/use-xtreme-push';

import { AuthProvider } from './components/auth-provider/auth-provider';
import { GraphProvider } from './components/graph-provider/graph-provider';
import { LocaleProvider } from './components/locale-provider/locale-provider';
import { useAuthController } from './hooks/use-auth-controller';
import { AppRouter } from './main.routes';
import { store, useAppDispatch } from './store/root.store';
import { isInApp } from './util/is-app';
import { LayoutContextProvider } from './util/layout-context';

const KeycloakMain = lazy(() =>
    import('../keycloak/main').then(({ KeycloakMain }) => ({ default: KeycloakMain })),
);

export const Application: FCWithChildren<unknown> = memo(({ children }) => {
    const { isFullscreen } = useFullscreen();
    const auth = useAuth();

    useCookieBanner();
    useGeoLocationToast();
    useAuthController();
    getXtremePush();
    useSetSource();

    const eventDispatch = useEventDispatcher();

    useResize(
        useMemo(
            () =>
                ({ width, height }) => {
                    eventDispatch({ name: EventName.WINDOW_RESIZE, width, height });
                },
            [eventDispatch],
        ),
    );

    useTransactionComplete(
        useMemo(
            () => (transaction) => {
                eventDispatch({
                    name:
                        transaction.operationType === 'credit'
                            ? EventName.USER_DEPOSIT
                            : EventName.USER_WITHDRAWAL,
                    amount: transaction.amount,
                    currency: transaction.currencyCode,
                });
            },
            [eventDispatch],
        ),
    );

    const location = useFullLocation();

    useEffect(() => {
        eventDispatch({
            name: EventName.NAVIGATE,
            path: location,
        });
    }, [location, eventDispatch]);

    useEventListener(sportncoEventListener);
    useEventListener(xtremePushEventListener);

    useEffect(() => {
        if (auth.isAuthenticated && auth.user && window?.xtremepush) {
            const userProfileId = auth.user?.profile.user_profile_id as string;
            const userEmail = auth.user?.profile.email;
            const phoneNumber = auth.user?.profile?.phone_number;

            window?.xtremepush('set', 'user_id', userProfileId);
            window?.xtremepush('set', 'email', userEmail);
            if (phoneNumber) {
                window?.xtremepush('set', 'mobile_number', phoneNumber ?? '');
            }
        }
    }, [auth]);

    useEffect(() => {
        document.body.classList.toggle('is-fullscreen', isFullscreen);
        return () => {
            document.body.classList.remove('is-fullscreen');
        };
    }, [isFullscreen]);

    const dispatch = useAppDispatch();

    // Reset store states on logout.
    const isLoggedOut = !auth.isAuthenticated && !auth.isLoading && !auth.user;
    useEffect(() => {
        if (isLoggedOut) {
            dispatch(accountStore.actions.reset());
        }
    }, [isLoggedOut, dispatch]);

    return <>{children}</>;
});

// Set this to true to enable debug mode when developing keycloak themes.
// We do this to avoid including the entire keycloak build in the initial bundle.
const keycloakDebugMode = false;

// Entry point for the application
export const Main = memo(() => {
    const isKeycloak =
        (window as unknown as { kcContext: unknown }).kcContext !== undefined || keycloakDebugMode;

    const geoIntegration = useMemo(
        () =>
            isInApp()
                ? createSportNCoNativeXPointIntegration(config.SPORT_ENDPOINT)
                : createXPointIntegration(config.GEOLOCATION.CLIENT_KEY),
        [],
    );

    if (isKeycloak) {
        return <KeycloakMain />;
    }

    // TODO: Haduken providers list needs to go... Flattern via a provider pipe component at some point.
    return (
        <EventingServiceProvider>
            <HelmetProvider>
                <ReduxProvider store={store}>
                    <LocaleProvider>
                        <BrowserRouter>
                            <AuthProvider>
                                <GeolocationContextProvider integration={geoIntegration}>
                                    <VerificationContextProvider>
                                        <GraphProvider>
                                            <PaymentsProvider>
                                                <LayoutContextProvider>
                                                    <Application>
                                                        <ScrollToTop />
                                                        <AppRouter />
                                                    </Application>
                                                </LayoutContextProvider>
                                            </PaymentsProvider>
                                        </GraphProvider>
                                    </VerificationContextProvider>
                                </GeolocationContextProvider>
                            </AuthProvider>
                        </BrowserRouter>
                    </LocaleProvider>
                </ReduxProvider>
            </HelmetProvider>
        </EventingServiceProvider>
    );
});
